/**
 * ParticipantPillList — renders a labeled row of participant pills via the
 * canonical EntityBadgeGroup primitive.
 *
 * Each participant is either:
 *  - A resolved CRM object (user/contact) → clickable badge that opens the
 *    object's slideout.
 *  - A plain email (not yet resolved to a CRM object) → clickable badge that
 *    opens the Create Contact modal with the email prefilled.
 *
 * Resolved entities sort first; the row caps at MAX_VISIBLE_PARTICIPANTS
 * with a clickable `+N` overflow pill to expand inline.
 *
 * Used by:
 *  - MeetingSummary (summary-section.tsx) as "Attendees"
 *  - EmailThreadParticipantsStack (email-thread-participants-stack.tsx) as
 *    "Participants" inside the thread header's popover
 *
 * RSVP status (when set) maps to the canonical `EntityBadgeData.status`
 * field and renders as a coloured dot on each badge's avatar.
 */

import { useCallback, useMemo } from 'react';

import { EntityBadgeGroup } from '@/lib/crm/components/badges/EntityBadgeGroup';
import type {
  EntityBadgeData,
  EntityBadgeGroupState,
  EntityBadgeStatus,
} from '@/lib/crm/components/badges/types';
import { useOpenCreateContact } from '@/lib/crm/hooks/useOpenCreateContact';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import {
  getEmailIndexSnapshot,
  getEntityPrimaryEmail,
  resolveEntityId,
} from '@/lib/crm/store/crmObjectStore';
import { getGravatarUrl } from '@/lib/crm/utils/avatar';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

// Max number of pills before the row collapses into a clickable `+N` overflow.
// Tuned to show most typical meeting attendee / email-thread participant lists
// in full before truncating; larger lists still collapse behind the expand pill.
const MAX_VISIBLE_PARTICIPANTS = 8;

// Synthetic prefix used to encode unresolved-email participants into the
// `EntityBadgeGroup`'s id-keyed onClick callback. Strip it when handling
// the click to recover the email.
const UNRESOLVED_ID_PREFIX = 'unresolved:';

export type RsvpStatus = 'accepted' | 'declined' | 'tentative' | 'needs_action';

/**
 * Map an RSVP value to the canonical `EntityBadgeStatus` rendered as a
 * dot on the badge avatar. Unknown statuses skip the dot rather than
 * default to a misleading colour.
 */
const RSVP_STATUS_BY_VALUE: Record<RsvpStatus, EntityBadgeStatus> = {
  accepted: { color: 'success', label: 'Accepted' },
  declined: { color: 'error', label: 'Declined' },
  tentative: { color: 'warning', label: 'Maybe' },
  needs_action: { color: 'neutral', label: 'Pending' },
};

function statusForRsvp(
  rsvp: RsvpStatus | string | undefined
): EntityBadgeStatus | undefined {
  if (!rsvp) {
    return undefined;
  }
  return RSVP_STATUS_BY_VALUE[rsvp as RsvpStatus];
}

export interface Participant {
  /** Stable React key. Required because email/entity_id may collide across items. */
  key: string;
  /**
   * If present, renders a clickable badge that opens the object's slideout.
   * Takes precedence over `email`.
   */
  object?: CrmObject;
  /**
   * Raw email address. Used when `object` is absent to render a pill that
   * opens the Create Contact modal on click with the email prefilled.
   * When `object` is also present, this is ignored (object wins).
   */
  email?: string;
  /**
   * Display name override. For plain-email pills, defaults to the email
   * local-part when absent. For resolved objects, overrides the object's
   * display name — used e.g. to label the viewer's own pill "You" so the
   * participant row and the conversation speak the same identity.
   */
  name?: string;
  /**
   * Optional RSVP status. Mapped to a coloured status dot rendered on
   * the badge's avatar (success/warning/error/neutral). Unknown values
   * skip the dot.
   */
  rsvpStatus?: RsvpStatus | string;
}

interface ParticipantPillListProps {
  /** Section heading rendered above the pills. */
  label: string;
  /** Participants to display. Empty list renders nothing. */
  participants: Participant[];
  /** Org ID for the Create Contact flow (required when plain-email pills are clickable). */
  orgId: number;
}

// Look up the avatar URL on a CrmObject in the same priority order the
// canonical helper (`crmObjectToEntityBadgeData`) uses.
function getObjectAvatarUrl(obj: CrmObject): string | undefined {
  const props = obj.properties ?? {};
  // CRM obj.properties[key]: single-level {value} wrapper — unwrap via getPropertyValue
  const value = getPropertyValue<unknown>(props.ahoy_profile_photo_url);
  const fallback = getPropertyValue<unknown>(props.ahoy_avatar_url);
  if (typeof value === 'string' && value) {
    return value;
  }
  if (typeof fallback === 'string' && fallback) {
    return fallback;
  }
  return undefined;
}

export function ParticipantPillList({
  label,
  participants,
  orgId,
}: ParticipantPillListProps) {
  // Origin-aware open (see useOpenSlideout): layers only when the pill is
  // inside a rendered entity/thread slideout; replaces otherwise.
  const openSlideout = useOpenSlideoutOptional();
  const { openCreateContact } = useOpenCreateContact(orgId);

  // Build the EntityBadgeData[] for the group, with resolved entities sorted
  // first. Each unresolved-email participant gets a synthetic id that the
  // click handler decodes back to the email + opens the create modal.
  const state = useMemo<EntityBadgeGroupState>(() => {
    const resolved: EntityBadgeData[] = [];
    const unresolved: EntityBadgeData[] = [];

    for (const p of participants) {
      const status = statusForRsvp(p.rsvpStatus);
      if (p.object) {
        const obj = p.object;
        const objEmail = getEntityPrimaryEmail(obj);
        resolved.push({
          id: obj.entity_id,
          // Preserve caller-supplied React key — the same entity_id may
          // legitimately repeat across roles (e.g. From and To on an
          // email thread).
          key: p.key,
          entityType: obj.entity_type_name,
          // Caller-supplied override wins (e.g. "You" for the viewer's own
          // pill); otherwise the object's canonical display name.
          displayName: p.name || getDisplayName(obj),
          avatarUrl: getObjectAvatarUrl(obj),
          status,
          // Pass email through for the hover-preview body so it can render
          // even before the store hydration completes.
          email: objEmail,
        });
      } else if (p.email) {
        const displayName = p.name || p.email.split('@')[0] || p.email;
        unresolved.push({
          // Encode email in the id so the group-level onClick can route
          // create-modal vs. slideout based on the badge that was clicked.
          id: `${UNRESOLVED_ID_PREFIX}${p.email}`,
          // Preserve caller-supplied React key — same email may repeat.
          key: p.key,
          // Render with the contact type purely for person-badge visuals
          // (avatar / icon). The synthetic `unresolved:` id makes
          // `HoverPreviewBadge` skip the hover popover, so no entity-bound
          // actions surface; clicking the pill opens the Create Contact modal.
          entityType: 'ahoy_contact',
          displayName,
          avatarUrl: getGravatarUrl(p.email, 48),
          status,
          email: p.email,
        });
      }
    }

    return { kind: 'data', data: [...resolved, ...unresolved] };
  }, [participants]);

  const handleClick = useCallback(
    (id: string) => {
      // Unresolved-email branch — the participant came in as a raw email
      // (no `object` from `useAssociations`). Two-way dispatch:
      //   1. If the email already maps to an entity in the store's
      //      email index (a previous search-and-seed flow indexed it),
      //      open that entity's slideout. Same path as the
      //      resolved-entity branch.
      //   2. Otherwise fall back to the Create Contact form. Delegates
      //      to `useOpenCreateContact` so this surface stays in
      //      lock-step with EntityBadgeByEmail. Only ONE place defines
      //      the create config; this file just supplies the prefill.
      if (id.startsWith(UNRESOLVED_ID_PREFIX)) {
        const email = id.slice(UNRESOLVED_ID_PREFIX.length);
        const indexed = getEmailIndexSnapshot(email);
        if (typeof indexed === 'string') {
          // Resolve through aliases (post-merge canonical) and open slideout.
          openSlideout?.(resolveEntityId(indexed));
          return;
        }

        openCreateContact({ ahoy_email: [email] });
        return;
      }

      // Resolved-entity branch — open the slideout when the context exists.
      openSlideout?.(id);
    },
    [openCreateContact, openSlideout]
  );

  if (state.kind === 'data' && state.data.length === 0) {
    return null;
  }

  return (
    <div>
      <p className='text-xs md:text-sm font-medium text-[var(--color-text-primary)] mb-2.5'>
        {label}
      </p>
      <EntityBadgeGroup
        state={state}
        // orgId is required for HoverPreviewBadge to wire up the hover
        // popover — without it, the group renders bare EntityBadges with
        // no hover behavior. Email-thread participants, meeting attendees,
        // and every other surface that goes through this list want the
        // popover (Draft email + entity identity).
        orgId={orgId}
        variant='default'
        avatar
        maxVisible={MAX_VISIBLE_PARTICIPANTS}
        onClick={handleClick}
        showEmail
      />
    </div>
  );
}
